import gsap from 'gsap';
import { createApp, onMounted, ref } from 'vue';

import { useQuickView } from '~/composables/use-quick-view';
import { ANIMATION_CONFIGS } from '~/constants/animations';

import * as AnalyticsData from '../analytics/analytics-data';
import { useVideo } from '../composables/use-video';
import { BaseSection } from './base-section';

const SECTION_NAME = 'color-grid';

/**
 * Color Grid Section
 *
 * This section displays a color grid with hero images, videos, and/or products.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class ColorGrid extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const videoCards = container.querySelectorAll('[data-block-type="video-card"]');
    const productCards = container.querySelectorAll('[data-block-type="product-card"]');
    const sectionInstance = this;

    createApp({
      name: 'ColorGridSection',
      setup() {
        const videoRefs = ref({});
        const quickViewRefs = ref({});

        onMounted(() => {
          videoCards.forEach((videoCard) => {
            const videoId = videoCard.dataset.videoId;
            if (!videoId) return;
            const videoComposable = useVideo(videoId);
            videoComposable.init();
            videoRefs.value[videoId] = videoComposable;
          });

          productCards.forEach((productCard) => {
            const productId = productCard.dataset.productId;
            if (productCard.dataset.enableQuickView === 'true' && productId) {
              quickViewRefs.value[productId] = useQuickView({
                closeOnClickOutside: productCard.dataset.closeQuickViewOnClickOutside === 'true',
                closeOnEscapeKey: productCard.dataset.closeQuickViewOnEscapeKey === 'true',
                id: productId,
                isOpenByDefault: productCard.dataset.openQuickViewByDefault === 'true',
              });
            }
          });
          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        return { quickViewRefs, videoRefs };
      },
    }).mount(container);
  }

  onUnload(instanceData) {
    // Kill ScrollTrigger instances
    if (instanceData.animation && instanceData.animation.scrollTrigger) {
      instanceData.animation.scrollTrigger.kill();
    }

    // Kill the animation
    if (instanceData.animation) {
      instanceData.animation.kill();
      instanceData.animation = null;
    }

    const app = instanceData.container.__vue_app__;
    if (app) {
      app.unmount();
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) return null;

    tl.fromTo(container, { opacity: 0 }, ANIMATION_CONFIGS.FADE_IN);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ColorGrid();
});
